import { $, html, View } from 'rune-ts';
import klass from './FanLetterItem.module.scss';
import { MShopAppStoreCommunityConstantS } from '../../../../../../modules/MShop/App/Store/Community/S/Constant/module/MShopAppStoreCommunityConstantS';
import { formatTimeElapsed } from '../../../../shared/util/date';
import { font, staticFont } from '../../../../shared/typography';
import { UtilS } from '../../../../../../modules/Util/S/Function/module/UtilS';
import { getResizedUrl } from '../../../../shared/util/image';

import { FanLetterItemButtonHeartView } from './views/FanLetterItemButtonHeartView';
import { FanLetterItemOpenDetailEvent } from './event/FanLetterItemEvent';
import { FanLetterData } from '../../../../features/FanLetter/type';

export type FanLetterCommentUser = { profile_image: string | null };

interface FanLetterOption {
  klass?: string;
  profile_horizontal: boolean;
  mode: 'XLarge' | 'Large' | 'Medium' | 'Small';
}

export class FanLetterItemView extends View<FanLetterData> {
  private static COMMENT_USERS_MAX_LENGTH = 3;

  private state: FanLetterOption;

  constructor(data: FanLetterData, option: FanLetterOption) {
    super(data, option);
    this.state = {
      klass: option?.klass || '',
      profile_horizontal: option.profile_horizontal,
      mode: option.mode,
    };
  }

  private _getProfileImageSrc() {
    return getResizedUrl({
      url: this.data.$one.users.profile_image || MShopAppStoreCommunityConstantS.EMPTY_USER_IMG,
      width: 96,
      format: 'webp',
      quality: 70,
    });
  }

  private _getMode() {
    switch (this.state.mode) {
      case 'XLarge':
        return klass.x_large;
      case 'Large':
        return klass.large;
      case 'Medium':
        return klass.medium;
      case 'Small':
        return klass.small;
    }
  }

  private _getBgKlass() {
    switch (this.data.bg_color) {
      case 'bg_1':
        return klass.bg_1;
      case 'bg_2':
        return klass.bg_2;
      case 'bg_3':
        return klass.bg_3;
      case 'bg_4':
        return klass.bg_4;
      case 'bg_5':
        return klass.bg_5;
    }
  }

  private static _GetCommentUserImageSrc(profile_image: string | null) {
    if (profile_image?.indexOf('s3.marpple.co')) {
      return getResizedUrl({
        url: profile_image || MShopAppStoreCommunityConstantS.EMPTY_USER_IMG,
        width: 48,
        format: 'webp',
        quality: 70,
      });
    }
    return profile_image || MShopAppStoreCommunityConstantS.EMPTY_USER_IMG;
  }

  private _templateCommentUsers() {
    if (this.data.comment_users.length) {
      return html`<div class="${klass.comment_users}">
        ${this.data.comment_users
          .slice(0, FanLetterItemView.COMMENT_USERS_MAX_LENGTH)
          .map(({ profile_image }, idx) => {
            return html` <div
              class="${klass.comment_user}"
              style="z-index: ${FanLetterItemView.COMMENT_USERS_MAX_LENGTH - idx};"
            >
              <img src="${FanLetterItemView._GetCommentUserImageSrc(profile_image)}" alt="" />
            </div>`;
          })}
      </div>`;
    }
    return html``;
  }

  public setLikeCount(like_count: number) {
    this.data.like_count = like_count;
    return this;
  }

  public setIsLike(is_liked: boolean) {
    this.data.is_liked = is_liked;
    return this;
  }

  public setCommentCount(comment_count: number) {
    this.data.comment_count = comment_count;
    return this;
  }

  public setCommentUsers(comment_users: FanLetterCommentUser[]) {
    this.data.comment_users = comment_users;
    return this;
  }

  override template() {
    const {
      created_at = new Date(),
      body,
      photos,
      comment_count,
      user_id,
      $one: { users, stores },
    } = this.data;
    const photo_url = photos[0]?.url;
    const is_small_mode = this.state.mode == 'Small';
    return html`
      <div class="${this.state.klass} ${this._getMode()}">
        <div class="${klass.fan_letter_item} ${this._getBgKlass()}">
          <div class="${klass.profile}">
            <div class="${klass.profile_left}">
              <img src="${this._getProfileImageSrc()}" class="${klass.profile_img}" alt="" />
            </div>
            <div
              class="${klass.profile_right} ${this.state.profile_horizontal ? klass.profile_right__flex : ''}"
            >
              <div class="${klass.nick_name} ${font(is_small_mode ? '12_bold' : '14_bold')}">
                ${html.preventEscape(user_id == stores.user_id ? stores.name : users.nick_name)}
              </div>
              <div class="${klass.profile_date} ${font(is_small_mode ? '10_regular' : '12_regular')}">
                ${formatTimeElapsed((created_at || new Date()).toString())}
              </div>
            </div>
          </div>
          <div class="${klass.body_photo}">
            <div class="${klass.body} ${font(is_small_mode ? '12_medium' : '14_medium')}">
              ${html.preventEscape(UtilS.linkify(body))}
            </div>
            ${photo_url
              ? html`<div class="${klass.photo}">
                  <img
                    src="${getResizedUrl({
                      url: photo_url,
                      width: 300,
                      height: 300,
                      format: 'webp',
                      quality: 70,
                    })}"
                    alt=""
                  />
                </div>`
              : ''}
          </div>
          <div class="${klass.bottom}">
            <div class="${klass.replies} ${staticFont('unica_14_medium')}">
              ${this._templateCommentUsers()}
              <div class="${klass.comment_count}">
                ${ET('creator_shop::fan_letters::replies', { comment_count })}
              </div>
            </div>
            <div class="${klass.like}">
              ${new FanLetterItemButtonHeartView({
                count: this.data.like_count || 0,
                is_active: this.data.is_liked,
                size: 'medium',
                type: 'transparent',
                klass: klass.btn_heart,
                is_login: this.data.is_login,
                fan_letter_id: this.data.id,
                store_id: this.data.store_id || 0,
              })}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  protected override onRender() {
    this.addEventListener('click', (e) => {
      if (!$(e.target).closest(`.${klass.like}`)) {
        this.dispatchEvent(FanLetterItemOpenDetailEvent, {
          bubbles: true,
          detail: {
            fan_letter_id: this.data.id,
            element: this.element(),
            fan_letter_item_view: this,
          },
        });
      }
    });
  }
}
